import React from 'react';
import PropTypes from 'prop-types';
import { ScreenReaderText } from '#/shared/components/Accessibility';
import { useGetRouteBasicsQuery } from '#/services/developerApi';
import { parseNumericalPortionOfRouteId } from '#/trip-planner/utils/itinerary';
import * as Styled from './styled';

const DEFAULT_SIZE = 32;

function RouteBadge({
  routeId,
  size,
  useScaling,
  useBorder,
  underline,
  className,
}) {
  const numericRouteId = parseNumericalPortionOfRouteId(routeId);

  const {
    routeColor,
    isShuttle,
    shortName: badgeText,
    desc: routeName,
  } = useGetRouteBasicsQuery(null, {
    selectFromResult: ({ data }) => data?.[numericRouteId] || {},
  });

  return routeColor ? (
    <Styled.RouteBadgeWrapper underline={underline && !isShuttle}>
      <Styled.RouteBadge
        routeColor={routeColor}
        size={size}
        textLength={badgeText.length}
        defaultSize={DEFAULT_SIZE}
        useInverseColors={isShuttle}
        useScaling={useScaling}
        useBorder={useBorder}
        className={className}
      >
        <span aria-hidden="true">{badgeText || ''}</span>
        <ScreenReaderText>{routeName}</ScreenReaderText>
      </Styled.RouteBadge>
    </Styled.RouteBadgeWrapper>
  ) : null;
}

RouteBadge.propTypes = {
  /** ID for of the route that the badge will represent */
  routeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** Height and width of badge in pixels */
  size: PropTypes.number,
  /**
   * If `true` the badge's total size including it border width is scaled to the
   * supplied `size` value, otherwise the border width is in addition to `size`
   */
  useScaling: PropTypes.bool,
  /** If `true` badge is given a border */
  useBorder: PropTypes.bool,
  /** If `true` non-inverse colored badges are given a curved bottom border */
  underline: PropTypes.bool,
  /** Pass through for styled-components */
  className: PropTypes.string,
};

RouteBadge.defaultProps = {
  size: DEFAULT_SIZE,
  useScaling: true,
  useBorder: false,
  underline: false,
  className: null,
};

export default RouteBadge;
